import mqtt from "mqtt";
import type { MqttClient } from "mqtt";
import {
  applyMQTTConfig,
  buildMQTTBrokerUrl,
  isMQTTEnabled,
  mqttConfig,
} from "./mqttConfig";
import type { MQTTConfig } from "./mqttConfig";

const LOG = "[StormcloudVideoPlayer][MQTT]";

export type MQTTStatus = "disconnected" | "connecting" | "connected" | "error";

let client: MqttClient | null = null;
let status: MQTTStatus = "disconnected";

export function getMQTTStatus(): MQTTStatus {
  return status;
}

export function isMQTTConnected(): boolean {
  return status === "connected" && client !== null && client.connected;
}

export function isMQTTConfigured(): boolean {
  return isMQTTEnabled();
}

export function configureMQTT(overrides: Partial<MQTTConfig>): void {
  applyMQTTConfig(overrides);
  disconnectMQTT();
}

export function initMQTTClient(): void {
  if (client || !isMQTTEnabled()) return;

  const url = buildMQTTBrokerUrl();
  status = "connecting";

  const clientId = `stormcloud-vp-${Math.random().toString(36).slice(2, 9)}`;

  try {
    client = mqtt.connect(url, {
      clientId,
      username: mqttConfig.username,
      password: mqttConfig.password,
      keepalive: 60,
      clean: true,
      reconnectPeriod: 5000,
      connectTimeout: 10_000,
      queueQoSZero: false,
    });
  } catch (err) {
    status = "error";
    console.warn(`${LOG} connect() threw:`, err);
    return;
  }

  client.on("connect", () => {
    status = "connected";
    console.info(`${LOG} connected to ${url}`);
  });

  client.on("reconnect", () => {
    status = "connecting";
    console.info(`${LOG} reconnecting…`);
  });

  client.on("offline", () => {
    status = "disconnected";
    console.warn(`${LOG} offline`);
  });

  client.on("error", (err) => {
    status = "error";
    console.warn(`${LOG} error:`, err.message);
  });

  client.on("close", () => {
    if (status === "connected") {
      status = "disconnected";
    }
  });
}

export function ensureMQTTClient(): void {
  if (isMQTTEnabled() && !client) {
    initMQTTClient();
  }
}

export function publishMQTT(
  topic: string,
  payload: Record<string, unknown>
): boolean {
  if (!isMQTTEnabled()) {
    return false;
  }

  ensureMQTTClient();

  if (!client) {
    return false;
  }

  try {
    client.publish(topic, JSON.stringify(payload), { qos: mqttConfig.qos });
    return true;
  } catch (err) {
    console.warn(`${LOG} publish failed on ${topic}:`, err);
    return false;
  }
}

export function disconnectMQTT(): void {
  if (client) {
    client.end(true);
    client = null;
    status = "disconnected";
  }
}
